import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    Tooltip,
    Typography,
} from '@mui/material';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';

import {
    fetchCart,
    removeCartItem,
    updateCartItem,
} from '../../actions/cartActions';
import { formatPrice } from '../../lib/money';
import { CartLine } from './CartPage';
import InCallCheckoutDialog from './InCallCheckoutDialog';

// El carrito del comprador para una tienda, sobre un vivo o una llamada 1:1:
// un botón con badge y un diálogo con las líneas y el subtotal. En la llamada
// 1:1 (`callRequestId`) y en el vivo (`live`) además tiene "Finalizar compra",
// que abre el checkout en un diálogo a pantalla completa encima
// (InCallCheckoutDialog): nunca se navega, así que la sesión de OpenTok
// sigue. En los dos se cambian cantidades y se quitan
// productos como en /carrito. Se relee cada vez que se abre (la
// compra rápida le agrega con el diálogo cerrado). `children` va debajo del
// carrito (la llamada pone ahí los pedidos de esta llamada); `extraCount` se
// suma al badge; `onPaid` / `onCheckoutOpened` / `onCheckoutClosed` avisan a
// la llamada, y `onCartChanged` cada vez que cambian las líneas, las cantidades
// o el subtotal (la llamada se lo señala al asesor, que ve el carrito en vivo).
// `variant`: 'fab' (la burbuja de la barra de la llamada) o 'button' (el
// rectángulo oscuro que hace juego con el botón salir del vivo).
const InCallCart = ({
    storeId,
    customer,
    storeCart,
    fetchCart,
    updateCartItem,
    removeCartItem,
    extraCount = 0,
    tooltip = 'Ver tu carrito',
    variant = 'fab',
    callRequestId = null,
    live = false,
    onPaid,
    onCheckoutOpened,
    onCheckoutClosed,
    onCartChanged,
    children,
}) => {
    const [open, setOpen] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const checkoutEnabled = Boolean((callRequestId || live) && customer);

    useEffect(() => {
        if (open && storeId && customer) fetchCart(storeId);
    }, [open, storeId, customer, fetchCart]);

    const cart = (storeCart && storeCart.cart) || null;
    const pendingItems = (storeCart && storeCart.pendingItems) || {};
    const busy = Object.keys(pendingItems).some((id) => pendingItems[id]);
    const badge = ((cart && cart.itemCount) || 0) + extraCount;

    const cartKey = cart
        ? `${cart.cartId}|${cart.itemCount}|${cart.subtotal}`
        : null;
    const cartKeyRef = useRef(cartKey);
    useEffect(() => {
        if (cartKeyRef.current === cartKey) return;
        cartKeyRef.current = cartKey;
        // También la primera lectura (el carrito se lee recién al abrirlo o al
        // agregar): de más, al asesor sólo le cuesta un refresco.
        if (cartKey !== null && onCartChanged) onCartChanged();
    }, [cartKey, onCartChanged]);

    const renderCart = () => {
        if (!customer) {
            return (
                <Typography color="text.secondary">
                    Iniciá sesión para ver tu carrito.
                </Typography>
            );
        }
        if (!cart) {
            return storeCart && storeCart.failed ? (
                <Typography color="text.secondary">
                    No pudimos cargar tu carrito.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            );
        }
        if (cart.items.length === 0) {
            return (
                <Typography color="text.secondary">
                    Tu carrito está vacío. Tocá un producto del catálogo para
                    agregarlo.
                </Typography>
            );
        }
        return (
            <Box>
                {cart.items.map((item) => (
                    <CartLine
                        key={item.cartItemId}
                        item={item}
                        tone="light"
                        pending={!!pendingItems[item.cartItemId]}
                        onQuantity={(line, quantity) =>
                            updateCartItem(storeId, line.cartItemId, quantity)
                        }
                        onRemove={(line) =>
                            removeCartItem(storeId, line.cartItemId)
                        }
                    />
                ))}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        pt: 2,
                        borderTop: '1px solid rgba(0,0,0,0.12)',
                    }}
                >
                    <Typography color="text.secondary">
                        Subtotal ({cart.itemCount}{' '}
                        {cart.itemCount === 1 ? 'producto' : 'productos'})
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.3rem' }}>
                        {formatPrice(cart.subtotal)}
                    </Typography>
                </Box>
                {cart.hasUnavailable && (
                    <Typography
                        color="text.secondary"
                        sx={{ fontSize: '0.8rem', textAlign: 'right' }}
                    >
                        No incluye los productos no disponibles.
                    </Typography>
                )}
            </Box>
        );
    };

    const badgeChip = badge > 0 && (
        <Chip
            label={badge}
            size="small"
            sx={{
                backgroundColor: variant === 'button' ? '#ff4444' : 'white',
                color: variant === 'button' ? '#fff' : 'black',
                fontWeight: 700,
                position: 'absolute',
                top: -7,
                right: -7,
                minWidth: 20,
                height: 20,
                fontSize: '0.75rem',
            }}
        />
    );

    const trigger =
        variant === 'button' ? (
            <Button
                variant="contained"
                onClick={() => setOpen(true)}
                aria-label={tooltip}
                sx={{
                    bgcolor: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    minWidth: 48,
                    height: 40,
                    position: 'relative',
                    border: '1px solid rgba(255,255,255,0.25)',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                }}
            >
                <ShoppingCartIcon sx={{ fontSize: 26 }} />
                {badgeChip}
            </Button>
        ) : (
            <Fab
                color="primary"
                onClick={() => setOpen(true)}
                aria-label={tooltip}
                sx={{ width: 56, height: 56, position: 'relative' }}
            >
                <ShoppingCartIcon />
                {badgeChip}
            </Fab>
        );

    return (
        <>
            <Tooltip title={tooltip} placement="top">
                {trigger}
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Tu carrito
                    <Typography
                        component="div"
                        color="text.secondary"
                        sx={{ fontSize: '0.9rem', fontWeight: 400 }}
                    >
                        {checkoutEnabled
                            ? live
                                ? 'Comprá sin salir del vivo.'
                                : 'Finalizá la compra sin cortar la llamada.'
                            : 'Lo pagás al terminar, desde Carrito.'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {renderCart()}
                    {children}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cerrar</Button>
                    {checkoutEnabled && cart && cart.items.length > 0 && (
                        <Button
                            variant="contained"
                            disabled={
                                busy ||
                                !cart.checkout ||
                                !cart.checkout.canCheckout
                            }
                            onClick={() => {
                                setOpen(false);
                                setCheckoutOpen(true);
                                if (onCheckoutOpened) onCheckoutOpened();
                            }}
                        >
                            Finalizar compra
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {checkoutEnabled && (
                <InCallCheckoutDialog
                    open={checkoutOpen}
                    storeId={storeId}
                    callRequestId={callRequestId}
                    live={live}
                    onPaid={onPaid}
                    onClose={() => {
                        setCheckoutOpen(false);
                        if (onCheckoutClosed) onCheckoutClosed();
                    }}
                />
            )}
        </>
    );
};

const mapStateToProps = (state, ownProps) => ({
    customer: state.customer.customer,
    storeCart:
        (ownProps.storeId && state.cart.byStore[ownProps.storeId]) || null,
});

export default connect(mapStateToProps, {
    fetchCart,
    updateCartItem,
    removeCartItem,
})(InCallCart);
